import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import {
  GITHUB_STUDENT_PACK,
  GITHUB_STUDENT_PACK_COOKIE,
  LOGIN_VARIANTS_ENDPOINT,
  LOGIN_VARIANTS_FILE_BASE_PATH,
  PREVIEW_LOGIN_VARIANTS_PARAM,
} from '../constants';
import { VariantProps } from '../interfaces/variant';
import { getVariant } from '../utils/variant-utils';
import { defaultVariant, githubLoginVariant } from '../login-variants';
import { setCookieValue, trackEvent } from '@do/seashell';

export type LoginVariantsHookResponse = {
  variant: VariantProps | null;
};

export type UseLoginVariantProps = {
  onboardingOrigin: string;
};

const getLoginVariantsJsonFilePath = () => {
  const shouldLoadPreview = new URLSearchParams(window.location.search).get(PREVIEW_LOGIN_VARIANTS_PARAM) === 'true';
  const jsonFilePath = `${LOGIN_VARIANTS_FILE_BASE_PATH}/${shouldLoadPreview ? 'stage2.json' : 'production.json'}`;
  return jsonFilePath;
};

const fetchLoginVariants = async (): Promise<VariantProps[]> => {
  try {
    const response = await fetch(`${LOGIN_VARIANTS_ENDPOINT}${getLoginVariantsJsonFilePath()}`);
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(`Failed fetching login variants: ${error}`);
  }
};

const useLoginVariants = ({ onboardingOrigin }: UseLoginVariantProps): LoginVariantsHookResponse => {
  const [loginVariant, setLoginVariant] = useState<VariantProps | null>(null);

  useEffect(() => {
    const fetchAndFindVariants = async () => {
      if (onboardingOrigin === GITHUB_STUDENT_PACK) {
        trackEvent('Login Variant', { variantType: githubLoginVariant.type });
        setCookieValue(GITHUB_STUDENT_PACK_COOKIE, 'true');
        setLoginVariant(githubLoginVariant);
        return;
      }

      try {
        const data = await fetchLoginVariants();
        const variant = getVariant(data);
        setLoginVariant(variant);
        trackEvent('Login Variant', { variantType: variant.type });
      } catch (err) {
        Sentry.captureException(err);
        console.error('unable to load variants', err);
        trackEvent('Login Variant', { variantType: defaultVariant.type });
        setLoginVariant(defaultVariant);
      }
    };
    fetchAndFindVariants();
  }, [onboardingOrigin]);

  return {
    variant: loginVariant,
  };
};

export default useLoginVariants;
